/* eslint-disable no-underscore-dangle */
import React from 'react';
import Button from '@prisjakt/ui-button';
import ArrowIcon from 'mdi-react/ArrowTopLeftIcon';
import Chevron from 'mdi-react/ChevronRightIcon';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';

import throttle from 'lodash.throttle';
import actions from '../../search/actions/search';
import AdContainer from '../../../components/ads';

import Overlay from '../../../components/overlay';

import * as S from './styles';

const adsViewUpdateAction = () => ({
  type: 'ADS_VIEW_UPDATE',
  view: `search_suggestions`,
});

class Suggestions extends React.PureComponent {
  id = 1;

  state = {
    selected: 0,
  };

  handleOutSideClick = e => {
    if (this.input && this.input === e.target) {
      return;
    }

    if (this.searchbar && this.searchbar.contains(e.target)) {
      return;
    }

    if (this.container && this.container.contains(e.target)) {
      return;
    }

    this.props.onClose();
  };

  updateAds = throttle(() => {
    this.props.adsViewUpdateAction();
  }, 1500);

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) {
      this.fetchSuggestions();
      this.updateAds();
    }
  }

  componentDidMount() {
    const activeElement = document.activeElement;
    if (activeElement.tagName !== 'INPUT') {
      return;
    }

    this.updateAds();

    /**
      Find references to search bar and input field
      in order to track key changes
    * */
    this.input = [
      ...document.querySelectorAll('[data-suggestions="search"]'),
    ].find(el => el.offsetParent !== null);

    this.searchbar = document.querySelector('[data-searchbar="true"]');

    this.enableKeyboardNavigation(activeElement);
    this.fetchSuggestions();
    window.addEventListener('click', this.handleOutSideClick);
  }

  componentWillUnmount() {
    if (this.input) {
      this.disableKeyboardNavigation(this.input);
    }
    window.removeEventListener('click', this.handleOutSideClick);
  }

  async fetchSuggestions() {
    const { query } = this.props;

    const { superSearchEndpoint } = this.context.market;

    if (!superSearchEndpoint) {
      return;
    }

    // HACK: Super search endpoint has rfe hardcoded at the moment
    // Fix this when refactoring full search area
    const endpoint = superSearchEndpoint.replace('/rfe', '');

    const res = await fetch(
      `${endpoint}/suggestions?q=${query}&market=${this.context.market.code}`
    );

    const data = await res.json();

    if (query !== this.props.query || data.error) {
      return;
    }

    const removeFirstHit =
      data.hits.hits.length > 1 &&
      data.hits.hits[0]._source.type === 'suggestion' &&
      `${data.hits.hits[0]._id} ` === query.toLowerCase();

    this.setState({
      suggestions: data.hits.hits
        .slice(removeFirstHit ? 1 : 0, removeFirstHit ? 11 : 10)
        .map(hit => ({
          text: hit._source.word,
          type: hit._source.type,
          id: hit._source.id,
          logo: hit._source.logo,
          // eslint-disable-next-line no-plusplus
          navId: this.id++,
        })),
    });
  }

  handleKeyPress = e => {
    const { suggestions = [], selected } = this.state;

    const selectable = [
      { navId: this.props.query, text: this.props.query },
      ...suggestions,
    ];

    switch (e.keyCode) {
      // Enter
      case 13: {
        const suggestion = selectable.find(s => s.navId === selected);

        if (suggestion) {
          this.handleClick(suggestion);
          this.input.blur();
          e.stopPropagation();
        } else {
          this.handleClick({ text: this.props.query, type: 'enter' });
          this.input.blur();
          e.stopPropagation();
        }
        break;
      }
      // Right
      case 39: {
        const suggestion = suggestions.find(s => s.navId === selected);

        if (
          suggestion &&
          this.input.selectionStart === this.input.selectionEnd &&
          this.input.selectionStart === this.input.value.length
        ) {
          this.input.value = `${suggestion.text} `;
          this.input.dispatchEvent(new CustomEvent('focus'));
        }
        // Make sure search bar updates text
        break;
      }
      // Up/Down
      case 38:
      case 40: {
        e.preventDefault();
        let nextItem;
        const currentIndex = selectable.findIndex(s => s.navId === selected);
        if (e.keyCode === 40) {
          nextItem = selectable[(currentIndex + 1) % selectable.length];
        } else {
          const nextIndex =
            currentIndex - 1 === -1 ? selectable.length - 1 : currentIndex - 1;

          nextItem = selectable[nextIndex];
        }

        if (nextItem) {
          this.input.value = `${nextItem.text} `;

          this.setState({ selected: nextItem.navId });
        }
        break;
      }
      default:
        break;
    }
  };

  disableKeyboardNavigation(input) {
    input.removeEventListener('keydown', this.handleKeyPress);
  }

  enableKeyboardNavigation(input) {
    input.addEventListener('keydown', this.handleKeyPress);
  }

  setSuggestion(suggestion) {
    this.input.value = `${suggestion.text.trim()} `;
    this.input.dispatchEvent(new CustomEvent('change'));
  }

  static async gotoUrl(type, id, query) {
    const res = await fetch(
      `${window.INTERNAL_HOSTNAME}/_internal/goto/${type}/${id}?q=${query}`
    );

    if (res.status !== 200) {
      throw new Error(`Cannot resolve url for ${type}`);
    }

    return res.text();
  }

  async handleClick(suggestion) {
    const { push } = this.props.history;
    const query = this.props.query;

    this.props.suggestionClick(suggestion.type);
    this.setSuggestion(suggestion);

    switch (suggestion.type) {
      // Try to resolve correct. If fail, fall through to default
      case 'product':
      case 'vcategory':
      case 'category':
      case 'brand_category':
      case 'store': {
        try {
          const url = await Suggestions.gotoUrl(
            suggestion.type,
            suggestion.id,
            query
          );
          this.props.onClose();
          push(url);
          break;
          // eslint-disable-next-line no-empty
        } catch (e) {}
      }
      // eslint-diable-next-line no-fallthrough
      default: {
        this.props.onClose();
        push(
          `/search?search=${encodeURIComponent(
            suggestion.text
          )}&searchInProps=${this.props.searchInProps}`
        );
      }
    }
  }

  getIndices(s) {
    const c = this.props.query.toLowerCase();
    return s.split('').reduce((a, e, i) => (e === c ? a.concat(i) : a), []);
  }

  render() {
    const { t } = this.context;
    const { suggestions, selected } = this.state;

    return (
      <>
        <S.Wrapper>
          <S.Inner>
            <S.Suggestions
              ref={node => {
                this.container = node;
              }}
            >
              <S.Item
                originalQuery
                disabled={this.props.query.length < 2}
                selected={this.props.query === selected}
                onClick={() => {
                  if (this.props.query.length < 2) {
                    return;
                  }
                  this.handleClick({
                    text: this.props.query,
                    type: 'original',
                  });
                }}
              >
                <S.OriginalQuery
                  selected={this.props.query === selected}
                  data-test="OriginalQuery"
                >
                  <span>
                    <T domain="search">suggest.title-original</T>{' '}
                    <i>{this.props.query}</i>
                  </span>
                  <Button
                    disabled={this.props.query.length < 2}
                    color="secondary"
                    variant="icon"
                    icon={<Chevron />}
                  />
                </S.OriginalQuery>
              </S.Item>
              {suggestions && suggestions.length >= 7 && (
                <S.AdItem style={{ order: 7 }}>
                  <AdContainer
                    key="dubbelpanorama1"
                    className="with-position-1"
                    placement="dubbelpanorama1"
                    min={0}
                    max={860}
                    async
                  />
                </S.AdItem>
              )}
              {suggestions &&
                suggestions.map((suggestion, i) => (
                  <S.Item
                    key={suggestion.navId}
                    hasArrow={suggestion.type === 'suggestion'}
                    onClick={() => {
                      this.handleClick(suggestion);
                    }}
                    selected={suggestion.navId === selected}
                    style={{ order: i }}
                    data-test="SearchSuggestionItem"
                  >
                    <>
                      <S.RowInfo>
                        <S.IconWrapper>
                          {suggestion.type === 'suggestion' && <S.SearchIcon />}
                          {suggestion.type === 'product' && (
                            <S.Icon
                              src={`https://cdn.pji.nu/product/standard/80/${suggestion.id}.jpg`}
                            />
                          )}
                          {suggestion.logo && suggestion.type === 'store' && (
                            <S.Icon storeLogo src={suggestion.logo} />
                          )}
                          {suggestion.logo &&
                            suggestion.type.indexOf('category') !== -1 && (
                              <S.Icon
                                src={`https://cdn.pji.nu/g/cat/192/${suggestion.logo}.png`}
                              />
                            )}
                        </S.IconWrapper>
                        {suggestion.text}
                      </S.RowInfo>
                      {suggestion.type !== 'suggestion' && (
                        <S.DirectLink data-test="DirectLink">
                          {t('search', `suggest.${suggestion.type}`)}
                        </S.DirectLink>
                      )}
                      {suggestion.type === 'suggestion' && (
                        <S.ExtendSearch>
                          <Button
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              this.setSuggestion(suggestion);
                              this.input.focus();
                            }}
                            color="secondary"
                            variant="icon"
                            icon={<ArrowIcon />}
                          />
                        </S.ExtendSearch>
                      )}
                    </>
                  </S.Item>
                ))}
            </S.Suggestions>
          </S.Inner>
        </S.Wrapper>
        <Overlay />
      </>
    );
  }
}

const { func, string, shape } = PropTypes;

Suggestions.propTypes = {
  query: string.isRequired,
  onClose: func.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  suggestionClick: func.isRequired,
};

Suggestions.contextType = AppContext;

export default connect(
  undefined,
  { ...actions, adsViewUpdateAction }
)(withRouter(Suggestions));
